import { useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Spinner } from "react-bootstrap";
import { useGetAnnouncementsDetail } from "../../../../hooks/organization/notification/useGetAnnouncementsDetail";
import { useMarkAnnouncementAsRead } from "../../../../hooks/organization/notification/useMarkAnnouncementAsRead";
import { useNotification } from "../../../../context/NotificationProvider";
import "./Notification.css";
import { handleSmartBack } from "../../../../utils/navigationHelper";

export const ReceivedNotificationDetail = () => {
  const { notificationId } = useParams();
  const navigate = useNavigate();
  const hasMarkedAsRead = useRef(false);

  const { announcement, loading, error, getAnnouncementDetail } =
    useGetAnnouncementsDetail();

  const { markAsRead } = useMarkAnnouncementAsRead();
  const { decrementUnreadCount } = useNotification();

  // Fetch notification detail
  useEffect(() => {
    if (notificationId) {
      getAnnouncementDetail(notificationId);
    }
  }, [notificationId]);

  // Mark as read if unseen and decrement count
  useEffect(() => {
    if (announcement && !announcement.read_at && !hasMarkedAsRead.current) {
      hasMarkedAsRead.current = true;
      markAsRead(announcement.id);
      decrementUnreadCount();
    }
  }, [announcement]);

  if (loading)
    return (
      <div className="page-center">
        <Spinner animation="border" />
      </div>
    );

  if (error) return <div className="error-box">{error}</div>;
  if (!announcement) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  /**
   * Get file extension from URL or filename
   * @param {string} url
   * @returns {string}
   */
  const getFileExtension = (url) => {
    if (!url) return "";
    return url.split(".").pop().split("?")[0].toLowerCase();
  };

  return (
    <div className="notification-page">
      {/* Top Section */}
      <div className="page-header">
        <div>
          <h2 className="page-title">{announcement.title}</h2>
          <p className="page-sub">
            Received: {formatDate(announcement.created_at)}
            {announcement.read_at && (
              <> • Read: {formatDate(announcement.read_at)}</>
            )}
          </p>
        </div>

        <Button className="primary-gradient-btn" onClick={() => handleSmartBack(navigate, "/school/notifications")}>
          Back
        </Button>
      </div>

      {/* Description Card */}
      <div className="soft-card lavender">
        <h6 className="section-title">Message</h6>
        <p className="section-text">{announcement.description}</p>
      </div>

      {/* Media Section */}
      {announcement.media_url && announcement.media_url.length > 0 && (
        <div className="soft-card mt-4">
          <h6 className="section-title">Attachments</h6>

          <div className="media-grid">
            {announcement.media_url.map((file, index) => {
              const fileUrl = file.url;
              const ext = getFileExtension(fileUrl);

              // IMAGE FILES
              if (["png", "jpg", "jpeg", "webp", "gif"].includes(ext)) {
                return (
                  <div key={index} className="media-card">
                    <img src={fileUrl} alt={`Attachment ${index + 1}`} />
                    <a
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="primary-gradient-btn small-btn mt-2"
                      style={{ display: "inline-block", textDecoration: "none" }}
                    >
                      Open
                    </a>
                  </div>
                );
              }

              // PDF FILE
              if (ext === "pdf") {
                return (
                  <div key={index} className="media-card pdf-card">
                    <div className="pdf-icon">📄</div>
                    <div className="file-name">Document {index + 1}</div>
                    <a
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="primary-gradient-btn small-btn"
                    >
                      Open PDF
                    </a>
                  </div>
                );
              }

              // OTHER FILE TYPES
              return (
                <div key={index} className="media-card pdf-card">
                  <div className="file-name">File {index + 1}</div>
                  <a
                    href={fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="primary-gradient-btn small-btn"
                  >
                    Download
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
